/**
 * PC Collection CSR — Full Client-Side Rendering
 * Uses GraphQL data + Liquid card HTML template for filtering/sorting/pagination.
 * All filtering happens in the browser — no AJAX calls after initial load.
 *
 * Dependencies:
 * - pc-collection-data.js (GraphQL fetcher, dispatches 'pc:products-ready')
 * - collection.pc-cards.liquid (Liquid template for card HTML via ?view=pc-cards)
 */
(function() {
  'use strict';

  var sidebar = document.querySelector('.pc-filter-sidebar');
  if (!sidebar) return;

  var collectionHandle = sidebar.dataset.collectionHandle;
  // Keep the storefront language prefix (e.g. "/en/" on the TH store): the card
  // HTML comes from the section rendering API, so without it the cards render in
  // the store's primary language even when the shopper is on /en/. Also keeps
  // history.replaceState from dropping the prefix from the address bar.
  var localeRoot = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
  var collectionUrl = localeRoot + '/collections/' + collectionHandle;

  var ITEMS_PER_LOAD = 21;
  var CARDS_FETCH_SIZE = 250;

  // Returns scroll offset accounting for sticky header (if enabled and would be active).
  // When sticky becomes position:fixed it's removed from flow, shifting content up by headerH.
  // If sticky will activate during the upcoming scroll, deduct extra to compensate.
  function getStickyOffset(targetY) {
    var h = document.getElementById('SiteHeader');
    if (!h || h.dataset.stickyEnabled !== 'true') return 20;
    if (targetY <= 50) return 20;
    var headerH = h.offsetHeight;
    var willShift = !h.classList.contains('scrolled');
    return willShift ? (2 * headerH + 10) : (headerH + 10);
  }

  var allProducts = [];    // From GraphQL: { id, handle, title, tags }
  var filteredProducts = [];
  var allCards = {};        // From Liquid: { handle: "<div>...</div>" }
  var isReady = false;
  var visibleCount = 0;     // How many products currently shown
  var activeFilters = {};   // { "franchise": ["franchise_Barrier Balancing"] }
  var tagLookup = {};       // { "franchise_barrier-balancing": "franchise_Barrier Balancing" }
  var cardTitles = {};      // From Liquid: { handle: "Localized product title" } (storefront language)
  var cardAvailable = {};   // From Liquid: { handle: true|false } — product.available (any variant available)
  var searchQuery = '';     // Normalized product-name search (lowercased, trimmed)
  var searchQueryRaw = '';  // As typed (trimmed) — used for URL + "no results" message

  // ============================================
  // i18n strings — rendered by Liquid (| t) into data attributes
  // ============================================
  var gridWrapper = document.querySelector('.pc-collection-grid-wrapper');
  var i18n = gridWrapper ? gridWrapper.dataset : {};
  var countElInit = document.querySelector('[data-collection-count]');
  var strShowingCount = (countElInit && countElInit.dataset.countTemplate) || 'Showing [start] - [end] of [count] products';
  var strNoResults = i18n.i18nNoResults || 'No products match the selected filters.';
  var strNoSearchResults = i18n.i18nNoSearchResults || 'No products match "[query]".';
  var strLoadMore = i18n.i18nLoadMore || 'Load More ([count])';
  // Section setting: push fully sold-out products to the end of the grid (default on)
  var oosLast = i18n.oosLast !== 'false';

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Normalize for name matching: lowercase + collapse whitespace (Thai has no case; substring match still works)
  function normalizeText(str) {
    return String(str || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  // Product matches the search box against the GraphQL title (default language)
  // OR the Liquid-rendered card title (storefront language, e.g. Thai on TH store).
  function matchesSearch(product) {
    if (!searchQuery) return true;
    if (normalizeText(product.title).indexOf(searchQuery) !== -1) return true;
    var localized = cardTitles[product.handle];
    return !!localized && normalizeText(localized).indexOf(searchQuery) !== -1;
  }

  // Feature banners: parsed from data-banner-data on grid element
  var featureBanners = [];  // [{ position: 4, imageUrl: "...", link: "#" }]

  // Match Shopify's handleize: lowercase, keep underscores, replace other non-alnum with hyphens
  function handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9_]+/g, '-').replace(/(^-|-$)/g, '');
  }


  // ============================================
  // Parse feature banners from grid data attribute
  // ============================================
  var grid = document.querySelector('.pc-product-grid');
  // Card API hardcodes per_row: 3, so banner must match that width
  var gridItemWidth = 'small--one-half medium-up--one-third';

  if (grid && grid.dataset.bannerData) {
    var bannerEntries = grid.dataset.bannerData.split('|||');
    bannerEntries.forEach(function(entry) {
      var parts = entry.split('||');
      if (parts.length >= 2 && parts[1]) {
        featureBanners.push({
          position: parseInt(parts[0], 10) || 1,
          imageUrl: parts[1],
          link: parts[2] || '#'
        });
      }
    });
  }

  function buildBannerHtml(banner) {
    return '<div class="grid__item grid-product pc-feature-banner-item ' + gridItemWidth + '">' +
      '<a href="' + banner.link + '" class="pc-feature-banner">' +
      '<img src="' + banner.imageUrl + '"' +
      ' alt="Featured" loading="lazy"' +
      ' class="pc-feature-banner__img">' +
      '</a></div>';
  }

  // ============================================
  // Show loading state in product grid
  // ============================================
  if (grid) {
    var skeletonHtml = '';
    for (var s = 0; s < 3; s++) {
      skeletonHtml += '<div class="pc-skeleton-card">' +
        '<div class="pc-skeleton-card__image pc-skeleton-shimmer"></div>' +
        '<div class="pc-skeleton-card__title pc-skeleton-shimmer"></div>' +
        '<div class="pc-skeleton-card__price pc-skeleton-shimmer"></div>' +
        '</div>';
    }
    grid.innerHTML = skeletonHtml;
  }

  // Grey out sidebar + thumbnails + sort until ready
  if (sidebar) sidebar.classList.add('pc-csr-not-ready');
  document.querySelectorAll('.pc-narrow').forEach(function(el) {
    el.classList.add('pc-csr-not-ready');
  });
  var sortWrapper = document.querySelector('.pc-toolbar__sort');
  if (sortWrapper) sortWrapper.classList.add('pc-csr-not-ready');
  var searchWrapper = document.querySelector('.pc-toolbar__search');
  if (searchWrapper) searchWrapper.classList.add('pc-csr-not-ready');

  // Hide Liquid pagination immediately
  var liquidPagination = document.querySelector('.pagination');
  if (liquidPagination) liquidPagination.style.display = 'none';


  // ============================================
  // Fetch card HTML from Liquid template
  // ============================================
  async function fetchAllCards() {
    var cardMap = {};
    var page = 1;
    var hasMore = true;

    while (hasMore) {
      try {
        var url = collectionUrl + '?page=' + page + '&sections=pc-card-api';
        var res = await fetch(url);
        var json = await res.json();

        // Section rendering returns { "pc-card-api": "<html>..." }
        var html = json['pc-card-api'] || '';
        if (!html.trim()) { hasMore = false; break; }

        var parser = new DOMParser();
        var doc = parser.parseFromString('<div>' + html + '</div>', 'text/html');
        var cards = doc.querySelectorAll('[data-pc-card]');

        if (cards.length === 0) { hasMore = false; break; }

        cards.forEach(function(card) {
          var handle = card.dataset.handle;
          if (handle) {
            cardMap[handle] = card.innerHTML.trim();
            if (card.dataset.title) cardTitles[handle] = card.dataset.title;
            if (card.dataset.available) cardAvailable[handle] = card.dataset.available === 'true';
          }
        });


        if (cards.length < CARDS_FETCH_SIZE) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (err) {
        console.error('[PC CSR] Card fetch error:', err);
        hasMore = false;
      }
    }

    return cardMap;
  }

  // ============================================
  // Render products into grid (reset)
  // ============================================
  function renderProducts(products) {
    if (!grid) return;

    visibleCount = Math.min(ITEMS_PER_LOAD, products.length);
    var visible = products.slice(0, visibleCount);

    var html = '';
    visible.forEach(function(product, i) {
      var productIndex = i + 1; // 1-based index
      // Insert banners whose position matches this index
      featureBanners.forEach(function(banner) {
        if (banner.position <= products.length && banner.position === productIndex) {
          html += buildBannerHtml(banner);
        }
      });
      var cardHtml = allCards[product.handle];
      if (cardHtml) html += cardHtml;
    });

    // Banners with position beyond visible count but within total products — append at end of visible batch
    featureBanners.forEach(function(banner) {
      if (banner.position > visibleCount && banner.position <= products.length) {
        // Will be inserted when their position becomes visible via loadMore
      } else if (banner.position > products.length) {
        // Beyond total product count — append at end
        html += buildBannerHtml(banner);
      }
    });

    if (products.length === 0) {
      var emptyMsg = searchQuery
        ? strNoSearchResults.replace('[query]', escapeHtml(searchQueryRaw))
        : strNoResults;
      html = '<div class="pc-csr-no-results">' + emptyMsg + '</div>';
    }

    grid.innerHTML = html;

    // Re-init lazy loading
    if (typeof lazySizes !== 'undefined') lazySizes.init();

    updateProductCount();
    renderLoadMore();
  }

  // ============================================
  // Load More — append next batch
  // ============================================
  function loadMore() {
    var start = visibleCount;
    var end = Math.min(start + ITEMS_PER_LOAD, filteredProducts.length);
    var nextBatch = filteredProducts.slice(start, end);

    var html = '';
    nextBatch.forEach(function(product, i) {
      var productIndex = start + i + 1; // 1-based global index
      // Insert banners whose position matches this index
      featureBanners.forEach(function(banner) {
        if (banner.position <= filteredProducts.length && banner.position === productIndex) {
          html += buildBannerHtml(banner);
        }
      });
      var cardHtml = allCards[product.handle];
      if (cardHtml) html += cardHtml;
    });

    // If this is the last batch, append banners beyond product count
    if (end >= filteredProducts.length) {
      featureBanners.forEach(function(banner) {
        if (banner.position > filteredProducts.length) {
          html += buildBannerHtml(banner);
        }
      });
    }

    grid.insertAdjacentHTML('beforeend', html);
    visibleCount = end;

    if (typeof lazySizes !== 'undefined') lazySizes.init();

    updateProductCount();
    renderLoadMore();
  }

  function updateProductCount() {
    var total = filteredProducts.length;
    var showing = Math.min(visibleCount, total);
    var text = strShowingCount
      .replace('[start]', total > 0 ? 1 : 0)
      .replace('[end]', showing)
      .replace('[count]', total);
    document.querySelectorAll('[data-collection-count]').forEach(function(el) {
      el.textContent = text;
    });
  }

  // ============================================
  // Load More button
  // ============================================
  function renderLoadMore() {
    var existing = document.querySelector('.pc-csr-load-more');
    if (existing) existing.remove();

    if (visibleCount >= filteredProducts.length) return;

    var remaining = filteredProducts.length - visibleCount;
    var html = '<div class="pc-csr-load-more">';
    html += '<button type="button" class="pc-csr-load-more__btn" data-csr-load-more>';
    html += strLoadMore.replace('[count]', remaining);
    html += '</button></div>';
    grid.insertAdjacentHTML('afterend', html);
  }

  // Load More click
  document.addEventListener('click', function(e) {
    if (!e.target.closest('[data-csr-load-more]') || !isReady) return;
    e.preventDefault();
    loadMore();
  });

  // ============================================
  // Filter: OR within group, AND between groups
  // ============================================
  function applyFilters() {
    var activeGroups = Object.keys(activeFilters).filter(function(g) {
      return activeFilters[g].length > 0;
    });

    // Search by name applies first (AND with all tag groups)
    var searchBase = allProducts.filter(matchesSearch);

    if (activeGroups.length === 0) {
      filteredProducts = searchBase;
      return;
    }

    filteredProducts = searchBase.filter(function(product) {
      // Must match ALL groups (AND)
      return activeGroups.every(function(group) {
        // Must match ANY tag within this group (OR)
        return activeFilters[group].some(function(rawTag) {
          return product.tags.indexOf(rawTag) !== -1;
        });
      });
    });
  }

  // ============================================
  // URL State
  // ============================================
  function pushUrlState(replace) {
    var params = new URLSearchParams();

    Object.keys(activeFilters).forEach(function(group) {
      activeFilters[group].forEach(function(rawTag) {
        var prefix = group + '_';
        var value = rawTag.replace(prefix, '');
        params.append('filter_' + group, handleize(value));
      });
    });

    if (currentSort && currentSort !== 'best-selling') {
      params.set('sort_by', currentSort);
    }

    if (searchQueryRaw) {
      params.set('q', searchQueryRaw);
    }

    var qs = params.toString();
    var newUrl = collectionUrl + (qs ? '?' + qs : '');
    // Typing in the search box replaces the current entry instead of pushing one per keystroke
    if (replace) {
      history.replaceState({ pcCsr: true }, '', newUrl);
    } else {
      history.pushState({ pcCsr: true }, '', newUrl);
    }
  }

  function restoreFromUrl() {
    var params = new URLSearchParams(window.location.search);
    activeFilters = {};

    params.forEach(function(value, key) {
      if (key.indexOf('filter_') === 0) {
        var group = key.replace('filter_', '');
        // URL has handleized value, lookup raw tag
        var handleKey = group + '_' + value;
        var rawTag = tagLookup[handleKey];
        if (rawTag) {
          if (!activeFilters[group]) activeFilters[group] = [];
          activeFilters[group].push(rawTag);
        }
      }
    });

    syncNarrowCards();
    syncSidebarCheckboxes();

    // Restore sort
    currentSort = params.get('sort_by') || 'best-selling';
    if (sortSelect) sortSelect.value = currentSort;

    // Restore search
    setSearchQuery(params.get('q') || '', true);
  }

  // Browser back/forward
  window.addEventListener('popstate', function() {
    if (!isReady) return;
    restoreFromUrl();
    applyFilters();
    applySorting();
    renderProducts(filteredProducts);
    updateFilterCounts();
    renderActiveFilters();
    updateGroupClearButtons();
  });

  // ============================================
  // Update filter counts + hide/disable 0-count
  // Sidebar list: hide 0-count items
  // Thumbnail (narrow): disable 0-count cards
  // ============================================
  function updateFilterCounts() {
    // Counts respect the current name search, then the OTHER groups' selections
    var searchBase = allProducts.filter(matchesSearch);

    // Sidebar checkboxes
    sidebar.querySelectorAll('input[type="checkbox"][data-tag]').forEach(function(cb) {
      var tag = cb.dataset.tag;
      var group = cb.dataset.group;

      // Count against products filtered by OTHER groups (not this one)
      var otherGroups = Object.keys(activeFilters).filter(function(g) {
        return g !== group && activeFilters[g].length > 0;
      });

      var baseProducts = otherGroups.length === 0 ? searchBase : searchBase.filter(function(product) {
        return otherGroups.every(function(g) {
          return activeFilters[g].some(function(t) {
            return product.tags.indexOf(t) !== -1;
          });
        });
      });

      var count = 0;
      baseProducts.forEach(function(product) {
        if (product.tags.indexOf(tag) !== -1) count++;
      });

      var li = cb.closest('.pc-filter-option');
      if (!li) return;

      var countEl = li.querySelector('.pc-filter-option__count');
      if (countEl) countEl.textContent = '(' + count + ')';

      // Sidebar list: hide 0-count (unless checked)
      if (count === 0 && !cb.checked) {
        li.style.display = 'none';
      } else {
        li.style.display = '';
      }
    });

    // Narrow thumbnail cards: disable 0-count
    document.querySelectorAll('.pc-narrow__card').forEach(function(card) {
      var handleTag = card.dataset.narrowTag;
      var rawTag = tagLookup[handleTag];
      if (!rawTag) return;

      var group = rawTag.substring(0, rawTag.indexOf('_'));

      // Count against OTHER groups
      var otherGroups = Object.keys(activeFilters).filter(function(g) {
        return g !== group && activeFilters[g].length > 0;
      });

      var baseProducts = otherGroups.length === 0 ? searchBase : searchBase.filter(function(product) {
        return otherGroups.every(function(g) {
          return activeFilters[g].some(function(t) {
            return product.tags.indexOf(t) !== -1;
          });
        });
      });

      var count = 0;
      baseProducts.forEach(function(product) {
        if (product.tags.indexOf(rawTag) !== -1) count++;
      });

      var isActive = card.classList.contains('pc-csr-active');
      if (count === 0 && !isActive) {
        card.classList.add('pc-narrow__card--disabled');
      } else {
        card.classList.remove('pc-narrow__card--disabled');
      }
    });

    // Update Show More counts
    sidebar.querySelectorAll('.pc-filter-group').forEach(function(groupEl) {
      var visibleItems = groupEl.querySelectorAll('.pc-filter-option:not([style*="display: none"])');
      var visibleLimit = parseInt(groupEl.querySelector('.pc-filter-show-more__more') ?
        groupEl.querySelector('.pc-filter-show-more__more').textContent.match(/\d+/) : 0) || 0;
      // Hide show more if no hidden items
      var showMoreBtn = groupEl.querySelector('[data-show-more]');
      if (showMoreBtn) {
        var hiddenCount = 0;
        groupEl.querySelectorAll('.pc-filter-option').forEach(function(li) {
          if (li.style.display !== 'none' && li.classList.contains('pc-filter-option--hidden') &&
              !groupEl.classList.contains('pc-filter-group--expanded')) {
            hiddenCount++;
          }
        });
        if (hiddenCount === 0) {
          showMoreBtn.style.display = 'none';
        } else {
          showMoreBtn.style.display = '';
        }
      }
    });
  }

  // ============================================
  // Active filter pills
  // ============================================
  function renderActiveFilters() {
    var existing = sidebar.querySelector('.pc-filter-refine');
    if (existing) existing.remove();

    var heading = sidebar.querySelector('.pc-filter-heading');
    var activeGroups = Object.keys(activeFilters).filter(function(g) {
      return activeFilters[g].length > 0;
    });

    if (activeGroups.length === 0) {
      if (heading) heading.style.display = '';
      return;
    }

    if (heading) heading.style.display = 'none';

    var html = '<div class="pc-filter-refine">';
    html += '<div class="pc-filter-refine__header">';
    html += '<span class="pc-filter-refine__title">REFINE BY</span>';
    html += '<a href="#" class="pc-filter-refine__clear-all" data-csr-clear-all>Clear All</a>';
    html += '</div>';
    html += '<div class="pc-filter-refine__tags">';

    activeGroups.forEach(function(group) {
      var groupEl = sidebar.querySelector('[data-filter-group="' + group + '"]');
      var groupTitle = groupEl ? groupEl.querySelector('.pc-filter-group__title').textContent.trim() : group;

      activeFilters[group].forEach(function(tag) {
        var prefix = group + '_';
        var display = tag.replace(prefix, '');
        html += '<a href="#" class="pc-filter-refine__tag" data-csr-remove-tag="' + tag + '" data-csr-remove-group="' + group + '">';
        html += groupTitle.toUpperCase() + ': ' + display.toUpperCase();
        html += ' <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">';
        html += '<path d="M8 2L2 8M2 2L8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>';
        html += '</svg></a>';
      });
    });

    html += '</div></div>';

    var inner = sidebar.querySelector('.pc-filter-sidebar__inner');
    if (inner) inner.insertAdjacentHTML('afterbegin', html);
  }

  // ============================================
  // Group clear buttons
  // ============================================
  function updateGroupClearButtons() {
    sidebar.querySelectorAll('[data-filter-group]').forEach(function(groupEl) {
      var prefix = groupEl.dataset.filterGroup;
      var clearBtn = groupEl.querySelector('.pc-filter-group__clear');
      var hasActive = activeFilters[prefix] && activeFilters[prefix].length > 0;

      if (hasActive && !clearBtn) {
        var header = groupEl.querySelector('.pc-filter-group__header');
        if (header) {
          header.insertAdjacentHTML('beforeend',
            '<a href="#" class="pc-filter-group__clear" data-csr-clear-group="' + prefix + '">Clear</a>');
        }
      } else if (!hasActive && clearBtn) {
        clearBtn.remove();
      }
    });
  }

  // ============================================
  // Sync narrow cards active state from activeFilters
  // ============================================
  function syncNarrowCards() {
    document.querySelectorAll('.pc-narrow__card').forEach(function(card) {
      var handleTag = card.dataset.narrowTag;
      var rawTag = tagLookup[handleTag];
      if (!rawTag) {
        card.classList.remove('pc-csr-active');
        return;
      }
      var group = rawTag.substring(0, rawTag.indexOf('_'));
      var isActive = !!(activeFilters[group] && activeFilters[group].indexOf(rawTag) !== -1);
      if (isActive) {
        card.classList.add('pc-csr-active');
      } else {
        card.classList.remove('pc-csr-active');
      }
    });
  }

  // ============================================
  // Sync sidebar checkboxes from activeFilters
  // ============================================
  function syncSidebarCheckboxes() {
    sidebar.querySelectorAll('input[type="checkbox"][data-tag]').forEach(function(cb) {
      var tag = cb.dataset.tag;
      var group = cb.dataset.group;
      cb.checked = activeFilters[group] && activeFilters[group].indexOf(tag) !== -1;
    });
  }

  // ============================================
  // Full render after any filter change
  // ============================================
  function onFilterChange() {
    applyFilters();
    applySorting();
    renderProducts(filteredProducts);
    updateFilterCounts();
    renderActiveFilters();
    updateGroupClearButtons();
    syncNarrowCards();
    syncSidebarCheckboxes();
    updateDrawer();
    pushUrlState();

    // Smooth scroll to collection content (includes narrow slider)
    var target = document.querySelector('.collection-content');
    if (target) {
      var targetY = target.getBoundingClientRect().top + window.scrollY;
      var offset = targetY - getStickyOffset(targetY);
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }

  // ============================================
  // Sidebar checkbox change
  // ============================================
  sidebar.addEventListener('change', function(e) {
    if (e.target.type !== 'checkbox' || !isReady) return;
    var tag = e.target.dataset.tag;
    var group = e.target.dataset.group;
    if (!tag || !group) return;

    if (!activeFilters[group]) activeFilters[group] = [];

    if (e.target.checked) {
      if (activeFilters[group].indexOf(tag) === -1) {
        activeFilters[group].push(tag);
      }
    } else {
      activeFilters[group] = activeFilters[group].filter(function(t) { return t !== tag; });
    }

    onFilterChange();
  });

  // ============================================
  // Clear All
  // ============================================
  document.addEventListener('click', function(e) {
    if (!e.target.closest('[data-csr-clear-all]') || !isReady) return;
    e.preventDefault();
    activeFilters = {};
    onFilterChange();
  });

  // ============================================
  // Clear group
  // ============================================
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-csr-clear-group]');
    if (!btn || !isReady) return;
    e.preventDefault();
    var group = btn.dataset.csrClearGroup;
    if (group) activeFilters[group] = [];
    onFilterChange();
  });

  // ============================================
  // Remove individual filter pill
  // ============================================
  document.addEventListener('click', function(e) {
    var pill = e.target.closest('[data-csr-remove-tag]');
    if (!pill || !isReady) return;
    e.preventDefault();
    var tag = pill.dataset.csrRemoveTag;
    var group = pill.dataset.csrRemoveGroup;
    if (group && activeFilters[group]) {
      activeFilters[group] = activeFilters[group].filter(function(t) { return t !== tag; });
    }
    onFilterChange();
  });

  // ============================================
  // Show More / Show Less
  // ============================================
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-show-more]');
    if (!btn) return;
    var group = btn.closest('.pc-filter-group');
    if (!group) return;
    var isExpanded = group.classList.toggle('pc-filter-group--expanded');
    var moreText = btn.querySelector('.pc-filter-show-more__more');
    var lessText = btn.querySelector('.pc-filter-show-more__less');
    if (moreText) moreText.style.display = isExpanded ? 'none' : '';
    if (lessText) lessText.style.display = isExpanded ? '' : 'none';
  });

  // ============================================
  // Search by product name
  // ============================================
  var searchInput = document.querySelector('[data-collection-search-input]');
  var searchClearBtn = document.querySelector('[data-collection-search-clear]');
  var searchTimer = null;

  // Set search state (+ sync clear button). syncInput=true also writes the value into the box.
  function setSearchQuery(value, syncInput) {
    searchQueryRaw = String(value || '').trim();
    searchQuery = normalizeText(searchQueryRaw);
    if (syncInput && searchInput && searchInput.value !== searchQueryRaw) searchInput.value = searchQueryRaw;
    if (searchClearBtn) searchClearBtn.hidden = !searchQueryRaw;
  }

  // Re-render after a search change. No scroll (user is typing); URL replaced, not pushed.
  function onSearchChange() {
    applyFilters();
    applySorting();
    renderProducts(filteredProducts);
    updateFilterCounts();
    updateDrawer();
    pushUrlState(true);
  }

  if (searchInput) {
    searchInput.addEventListener('input', function() {
      var value = this.value;
      if (searchClearBtn) searchClearBtn.hidden = !value.trim();
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function() {
        if (!isReady) return;
        setSearchQuery(value, false);
        onSearchChange();
      }, 250);
    });

    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        // Apply immediately; never submit a form / navigate away
        e.preventDefault();
        clearTimeout(searchTimer);
        if (!isReady) return;
        setSearchQuery(this.value, false);
        onSearchChange();
      } else if (e.key === 'Escape' && this.value) {
        e.preventDefault();
        clearTimeout(searchTimer);
        setSearchQuery('', true);
        if (isReady) onSearchChange();
      }
    });
  }

  if (searchClearBtn) {
    searchClearBtn.addEventListener('click', function() {
      clearTimeout(searchTimer);
      setSearchQuery('', true);
      if (isReady) onSearchChange();
      if (searchInput) searchInput.focus();
    });
  }

  // ============================================
  // Sort
  // ============================================
  var sortSelect = document.getElementById('PcSortBy');
  var currentSort = 'best-selling';

  // Build price/rating cache from card HTML (once after cards load)
  var cardDataCache = {}; // { handle: { price: 47.6, rating: 4.5, reviewCount: 1203 } }

  function buildCardDataCache() {
    Object.keys(allCards).forEach(function(handle) {
      var html = allCards[handle];
      if (!html) return;

      // Price: look for sale price first, then regular
      var price = 0;
      var priceMatch = html.match(/jsc-price-review__sale-price[^>]*>\$([\d,.]+)/);
      if (!priceMatch) priceMatch = html.match(/jsc-price-review__regular-price[^>]*>\$([\d,.]+)/);
      if (priceMatch) price = parseFloat(priceMatch[1].replace(',', ''));

      // Rating from data attributes on the product card wrapper
      var rating = 0;
      var reviewCount = 0;
      var ratingMatch = html.match(/data-rating="([\d.]+)"/);
      var countMatch = html.match(/data-review-count="(\d+)"/);
      if (ratingMatch) rating = parseFloat(ratingMatch[1]);
      if (countMatch) reviewCount = parseInt(countMatch[1]);

      cardDataCache[handle] = { price: price, rating: rating, reviewCount: reviewCount };
    });
  }

  function applySorting() {
    if (currentSort === 'price-ascending') {
      filteredProducts.sort(function(a, b) {
        var pa = (cardDataCache[a.handle] || {}).price || 0;
        var pb = (cardDataCache[b.handle] || {}).price || 0;
        return pa - pb;
      });
    } else if (currentSort === 'price-descending') {
      filteredProducts.sort(function(a, b) {
        var pa = (cardDataCache[a.handle] || {}).price || 0;
        var pb = (cardDataCache[b.handle] || {}).price || 0;
        return pb - pa;
      });
    } else if (currentSort === 'top-rated') {
      filteredProducts.sort(function(a, b) {
        var ca = (cardDataCache[a.handle] || {}).reviewCount || 0;
        var cb = (cardDataCache[b.handle] || {}).reviewCount || 0;
        return cb - ca;
      });
    }
    // best-selling / manual = original GraphQL order, already set by applyFilters()

    // Sold-out products (no available variant) go to the end, keeping the relative
    // order of both groups. Unknown availability (card not fetched) counts as in stock.
    if (oosLast) {
      var inStock = [];
      var soldOut = [];
      filteredProducts.forEach(function(product) {
        if (cardAvailable[product.handle] === false) soldOut.push(product);
        else inStock.push(product);
      });
      filteredProducts = inStock.concat(soldOut);
    }
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', function() {
      if (!isReady) return;
      currentSort = this.value;
      applyFilters();
      applySorting();
      renderProducts(filteredProducts);
      pushUrlState();

      var target = document.querySelector('.collection-content');
      if (target) {
        var targetY = target.getBoundingClientRect().top + window.scrollY;
        var offset = targetY - getStickyOffset(targetY);
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
    });
  }

  // ============================================
  // Mobile Filter Drawer
  // ============================================
  var drawer = document.getElementById('FilterDrawer');
  var drawerBody = document.getElementById('FilterDrawerBody');
  var drawerClose = drawer ? drawer.querySelector('.pc-filter-drawer__close') : null;
  var drawerOverlay = drawer ? drawer.querySelector('.pc-filter-drawer__overlay') : null;
  var drawerApply = drawer ? drawer.querySelector('.pc-filter-drawer__apply') : null;

  document.addEventListener('click', function(e) {
    if (e.target.closest('#MobileFilterToggle')) openDrawer();
  });

  function openDrawer() {
    if (!drawer || !drawerBody) return;
    var content = sidebar.querySelector('.pc-filter-sidebar__inner');
    if (content) {
      drawerBody.innerHTML = content.innerHTML;
      // Sync checkbox states
      drawerBody.querySelectorAll('input[type="checkbox"][data-tag]').forEach(function(dcb) {
        var scb = sidebar.querySelector('input[data-tag="' + dcb.dataset.tag + '"]');
        if (scb) {
          dcb.checked = scb.checked;
          dcb.disabled = scb.disabled;
        }
      });
    }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function updateDrawer() {
    if (!drawer || !drawer.classList.contains('is-open') || !drawerBody) return;
    var content = sidebar.querySelector('.pc-filter-sidebar__inner');
    if (content) {
      drawerBody.innerHTML = content.innerHTML;
      drawerBody.querySelectorAll('input[type="checkbox"][data-tag]').forEach(function(dcb) {
        var scb = sidebar.querySelector('input[data-tag="' + dcb.dataset.tag + '"]');
        if (scb) {
          dcb.checked = scb.checked;
          dcb.disabled = scb.disabled;
        }
      });
    }
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
  if (drawerApply) drawerApply.addEventListener('click', closeDrawer);

  // Drawer checkbox change — sync to sidebar and filter
  if (drawerBody) {
    document.getElementById('FilterDrawerBody').addEventListener('change', function(e) {
      if (e.target.type !== 'checkbox' || !isReady) return;
      var tag = e.target.dataset.tag;
      var group = e.target.dataset.group;
      if (!tag || !group) return;

      // Sync to sidebar
      var scb = sidebar.querySelector('input[data-tag="' + tag + '"]');
      if (scb) scb.checked = e.target.checked;

      if (!activeFilters[group]) activeFilters[group] = [];

      if (e.target.checked) {
        if (activeFilters[group].indexOf(tag) === -1) activeFilters[group].push(tag);
      } else {
        activeFilters[group] = activeFilters[group].filter(function(t) { return t !== tag; });
      }

      onFilterChange();

      // Update drawer content after filter change
      var content = sidebar.querySelector('.pc-filter-sidebar__inner');
      if (content) {
        drawerBody.innerHTML = content.innerHTML;
        drawerBody.querySelectorAll('input[type="checkbox"][data-tag]').forEach(function(dcb) {
          var scb2 = sidebar.querySelector('input[data-tag="' + dcb.dataset.tag + '"]');
          if (scb2) {
            dcb.checked = scb2.checked;
            dcb.disabled = scb2.disabled;
          }
        });
      }
    });
  }

  // ============================================
  // Narrow slider click
  // ============================================
  document.addEventListener('click', function(e) {
    var card = e.target.closest('.pc-narrow__card');
    if (!card || !card.dataset.narrowTag || !isReady) return;
    e.preventDefault();

    var handleTag = card.dataset.narrowTag;  // e.g. "franchise_barrier-balancing"
    var rawTag = tagLookup[handleTag];       // e.g. "franchise_Barrier Balancing"
    if (!rawTag) return;

    var group = rawTag.substring(0, rawTag.indexOf('_'));  // "franchise"
    var mode = card.dataset.narrowMode;
    var isActive = card.classList.contains('pc-csr-active');

    if (!activeFilters[group]) activeFilters[group] = [];

    if (isActive) {
      activeFilters[group] = activeFilters[group].filter(function(t) { return t !== rawTag; });
    } else {
      if (mode === 'thumbnail_single') {
        activeFilters[group] = [rawTag];
      } else {
        activeFilters[group].push(rawTag);
      }
    }

    onFilterChange();
  });


  // ============================================
  // INIT — Wait for GraphQL data, then fetch cards
  // ============================================
  // ============================================
  // Narrow Slider — Swiper init
  // ============================================
  function initNarrowSliders() {
    var narrowEls = document.querySelectorAll('.pc-narrow');
    if (!narrowEls.length || typeof Swiper === 'undefined') return;

    narrowEls.forEach(function(el) {
      var sliderId = el.id;
      var swiperEl = document.getElementById(sliderId + '-swiper');
      var controls = document.getElementById(sliderId + '-controls');
      var progressBar = document.getElementById(sliderId + '-progress');
      var prevBtn = document.getElementById(sliderId + '-prev');
      var nextBtn = document.getElementById(sliderId + '-next');
      if (!swiperEl) return;

      var swiper = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        freeMode: true,
        watchSlidesProgress: true,
        on: {
          init: function() { toggleControls(this); updateProgress(this); },
          resize: function() { toggleControls(this); },
          progress: function() { updateProgress(this); }
        }
      });

      function toggleControls(sw) {
        if (controls) controls.style.display = sw.virtualSize > sw.size ? 'flex' : 'none';
      }
      function updateProgress(sw) {
        var p = Math.max(0, Math.min(1, sw.progress));
        if (progressBar) progressBar.style.width = (p * 100) + '%';
        if (prevBtn) prevBtn.disabled = sw.isBeginning;
        if (nextBtn) nextBtn.disabled = sw.isEnd;
      }
      if (prevBtn) prevBtn.addEventListener('click', function() { swiper.slidePrev(); });
      if (nextBtn) nextBtn.addEventListener('click', function() { swiper.slideNext(); });
    });
  }

  // Wait for Swiper library then init
  if (typeof Swiper !== 'undefined') {
    initNarrowSliders();
    syncNarrowCards();
  } else {
    var swiperCheck = setInterval(function() {
      if (typeof Swiper !== 'undefined') {
        clearInterval(swiperCheck);
        initNarrowSliders();
        syncNarrowCards();
      }
    }, 100);
  }

  // ============================================
  // INIT
  // ============================================
  // Timeout — if data doesn't load in 10s, show retry
  var loadTimeout = setTimeout(function() {
    if (!isReady && grid) {
      grid.innerHTML = '<div class="pc-csr-retry">' +
        '<p>Products took too long to load.</p>' +
        '<button type="button" class="pc-csr-load-more__btn" onclick="window.location.reload()">Retry</button>' +
        '</div>';
    }
  }, 10000);

  document.addEventListener('pc:products-ready', function(e) {
    allProducts = e.detail.products;
    filteredProducts = allProducts.slice();

    // Build tag lookup: handleized → raw from all product tags
    allProducts.forEach(function(product) {
      product.tags.forEach(function(tag) {
        var h = handleize(tag);
        if (!tagLookup[h]) tagLookup[h] = tag;
      });
    });

    fetchAllCards().then(function(cards) {
      allCards = cards;
      isReady = true;
      clearTimeout(loadTimeout);
      buildCardDataCache();

      // Remove loading state from sidebar + thumbnails + sort
      if (sidebar) sidebar.classList.remove('pc-csr-not-ready');
      document.querySelectorAll('.pc-narrow').forEach(function(el) {
        el.classList.remove('pc-csr-not-ready');
      });
      var sortWrapper2 = document.querySelector('.pc-toolbar__sort');
      if (sortWrapper2) sortWrapper2.classList.remove('pc-csr-not-ready');
      var searchWrapper2 = document.querySelector('.pc-toolbar__search');
      if (searchWrapper2) searchWrapper2.classList.remove('pc-csr-not-ready');

      // Restore filters + sort from URL if any
      restoreFromUrl();
      applyFilters();
      applySorting();
      renderProducts(filteredProducts);
      updateFilterCounts();
      renderActiveFilters();
      updateGroupClearButtons();
    });
  });

})();
